import { useRef, useState, type ReactNode } from "react";
import SignatureCanvas from "react-signature-canvas";
import { IconPencil, IconPhoto } from "@tabler/icons-react";
import styles from "./SignaturePad.module.css";
import Button from "./Button";
import Callout from "./Callout";
import Text from "./Text";
import FileDropInput from "./FileDropInput";
// @ts-expect-error pure js
import Img from "./Img";
// @ts-expect-error pure js
import { preprocessImage } from "../../utils/documents";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { ALLOWED_MIME_TYPES } from "../../utils/constants/documents";
// @ts-expect-error pure js
import RadioGroup from "./RadioGroup";
// @ts-expect-error pure js
import RadioStandalone from "./RadioStandalone";

type Mode = "draw" | "import";
const maxFileSize = 1024 * 1024;

interface SignaturePadProps {
  onSave: (blob: Blob, filename: string) => Promise<void>;
}

const SIGNATURE_MIME_TYPES: string[] = ALLOWED_MIME_TYPES.filter(
  (type: string) => type.startsWith("image/"),
);

function dataURLToBlob(dataURL: string): Blob {
  const bytes = Uint8Array.from(atob(dataURL.split(",")[1]), (c) =>
    c.charCodeAt(0),
  );
  return new Blob([bytes], { type: "image/png" });
}

function validateSignatureImage(file: File): string | null {
  if (file.size > maxFileSize) {
    const maxSizeMo = maxFileSize / (1024 * 1024);
    return `Le fichier "${file.name}" est trop volumineux (max ${maxSizeMo} Mo)`;
  }
  if (!SIGNATURE_MIME_TYPES.includes(file.type)) {
    return `Le type de fichier "${file.name}" n'est pas autorisé (PNG, JPG, JPEG uniquement)`;
  }
  return null;
}

export default function SignaturePad({ onSave }: SignaturePadProps) {
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const [mode, setMode] = useState<Mode>("draw");
  const [importedFile, setImportedFile] = useState<File | null>(null);
  const [error, setError] = useState<ReactNode>("");
  const [saving, setSaving] = useState(false);

  function clearSignature() {
    sigCanvasRef.current?.clear();
    setError("");
  }

  function resetImport() {
    setImportedFile(null);
    setError("");
  }

  function switchMode(nextMode: Mode) {
    if (nextMode === mode) return;
    setError("");
    setMode(nextMode);
  }

  async function handleFiles(files: File[]) {
    const file = files[0];
    if (!file) return;
    const validationError = validateSignatureImage(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    try {
      const processed: File = await preprocessImage(file, false);
      setImportedFile(processed);
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (mode === "draw") {
        if (sigCanvasRef.current?.isEmpty()) {
          setError("Veuillez dessiner votre signature.");
          return;
        }
        const blob = dataURLToBlob(
          sigCanvasRef.current!.toDataURL("image/png"),
        );
        await onSave(blob, "signature.png");
      } else {
        if (!importedFile) {
          setError("Veuillez sélectionner une image.");
          return;
        }
        await onSave(importedFile, importedFile.name);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <RadioGroup>
        <RadioStandalone
          name="signature-mode"
          value="draw"
          checked={mode === "draw"}
          onChange={() => switchMode("draw")}
          label={
            <span className={styles.modeLabel}>
              <IconPencil size={16} />
              Dessiner
            </span>
          }
        />
        <RadioStandalone
          name="signature-mode"
          value="import"
          checked={mode === "import"}
          onChange={() => switchMode("import")}
          label={
            <span className={styles.modeLabel}>
              <IconPhoto size={16} />
              Importer une image
            </span>
          }
        />
      </RadioGroup>

      <div className={styles.content}>
        {mode === "draw" ? (
          <>
            <Text weight="medium" className={styles.title}>
              Reproduisez votre signature
            </Text>
            <div className={styles.canvasWrapper}>
              <SignatureCanvas
                ref={sigCanvasRef}
                canvasProps={{
                  width: 460,
                  height: 200,
                  className: styles.sigCanvas,
                }}
              />
            </div>
          </>
        ) : (
          <>
            <Text weight="medium" className={styles.title}>
              Importez une photo de votre signature
            </Text>
            <FileDropInput
              onFiles={handleFiles}
              accept={SIGNATURE_MIME_TYPES.join(",")}
              text={importedFile ? importedFile.name : "Sélectionnez une image"}
              format="Format PNG, JPG, JPEG"
            />
            {importedFile && (
              <Img
                blob={importedFile}
                alt="Aperçu de la signature"
                className={styles.preview}
              />
            )}
          </>
        )}
      </div>

      <Callout variant="danger" className={styles.errorCard}>
        {error}
      </Callout>

      <div className={styles.actions}>
        <Button
          onClick={mode === "draw" ? clearSignature : resetImport}
          variant="tertiary"
          type="button"
        >
          Réinitialiser
        </Button>
        <Button onClick={handleSave} isLoading={saving} type="button">
          Sauvegarder
        </Button>
      </div>
    </div>
  );
}
